"use client";

import { useState, useEffect } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { api } from '../utils/api';

export default function AddCustomerModal({ open, onClose, onAddParty }) {
  const [storeId, setStoreId] = useState(null);
  const [newParty, setNewParty] = useState({
    name: "",
    companyName: "",
    phone: "",
    address: "",
    billingAddress: "",
    shippingAddress: "",
    sameBillingShipping: true,
    creditLimit: 0,
    GSTIN: "",
    gstChecked: true,
    currentBalance: 0,
    isActive: true  // Added back isActive
  });
  const [showPartyDetails, setShowPartyDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [gstinError, setGstinError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Load storeId from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        if (parsedUserData.storeIds && parsedUserData.storeIds.length > 0) {
          setStoreId(parsedUserData.storeIds[0]); // Use first store ID
        }
      } catch (err) {
        console.error('Error parsing userData from localStorage:', err);
      }
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "sameBillingShipping") {
      setNewParty((prev) => ({
        ...prev,
        [name]: checked,
        billingAddress: checked ? prev.address : prev.billingAddress
      }));
    } else {
      setNewParty((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : (name === "creditLimit" || name === "currentBalance" ? Number.parseFloat(value) || 0 : value)
      }));

      // Auto-update billing address if sameBillingShipping is checked
      if (name === "address" && newParty.sameBillingShipping) {
        setNewParty((prev) => ({
          ...prev,
          billingAddress: value
        }));
      }

      // Validation
      if (name === "GSTIN") {
        const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (value.trim() === "") {
          setGstinError("GSTIN is required");
        } else if (!gstinRegex.test(value)) {
          setGstinError("Invalid GSTIN format (e.g., 27AAFCU5055K1ZO)");
        } else if (value.length !== 15) {
          setGstinError("GSTIN must be exactly 15 characters");
        } else {
          setGstinError("");
        }
      }

      if (name === "phone") {
        const phoneRegex = /^[6-9]\d{9}$/;
        if (value.trim() !== "" && !phoneRegex.test(value)) {
          setPhoneError("Please provide a valid Indian mobile number (e.g., 9876543210)");
        } else {
          setPhoneError("");
        }
      }
    }
  };

  const handleAddParty = async () => {
    // Validate storeId
    if (!storeId) {
      setError("Store ID not found. Please log in again.");
      return;
    }

    // Validate required fields
    if (!newParty.name.trim()) {
      setError("Customer Name is required");
      return;
    }

    if (!newParty.GSTIN.trim()) {
      setGstinError("GSTIN is required");
      setError("GSTIN is required");
      return;
    }

    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstinRegex.test(newParty.GSTIN)) {
      setGstinError("Invalid GSTIN format (e.g., 27AAFCU5055K1ZO)");
      setError("Invalid GSTIN format");
      return;
    }

    if (newParty.phone.trim() && !/^[6-9]\d{9}$/.test(newParty.phone)) {
      setPhoneError("Please provide a valid Indian mobile number (e.g., 9876543210)");
      setError("Invalid phone number");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Prepare payload matching API structure
      const payload = {
        name: newParty.name.trim(),
        companyName: newParty.companyName.trim() || undefined,
        phone: newParty.phone.trim() || undefined,
        address: newParty.address.trim() || undefined,
        billingAddress: newParty.billingAddress.trim() || undefined,
        shippingAddress: newParty.shippingAddress.trim() || undefined,
        sameBillingShipping: newParty.sameBillingShipping,
        creditLimit: newParty.creditLimit || 0,
        GSTIN: newParty.GSTIN.trim(),
        gstChecked: newParty.gstChecked,
        currentBalance: newParty.currentBalance || 0,
        isActive: newParty.isActive  // Include isActive in payload
      };

      // Remove undefined fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log('AddCustomerModal: Creating customer for storeId:', storeId, 'with payload:', payload);

      const res = await api.post(`/customers/${storeId}/create`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('AddCustomerModal: Response:', res.data);

      if (onAddParty && res.data) {
        onAddParty({
          id: res.data.id || res.data.data?.id,
          name: res.data.name || res.data.data?.name,
          phone: res.data.phone || res.data.data?.phone,
          address: res.data.address || res.data.data?.address,
          creditLimit: res.data.creditLimit || res.data.data?.creditLimit,
          currentBalance: res.data.currentBalance || res.data.data?.currentBalance,
          GSTIN: res.data.GSTIN || res.data.data?.GSTIN,
          isActive: res.data.isActive !== undefined ? res.data.isActive : true,
          companyName: res.data.companyName || res.data.data?.companyName,
          billingAddress: res.data.billingAddress || res.data.data?.billingAddress,
          shippingAddress: res.data.shippingAddress || res.data.data?.shippingAddress,
          sameBillingShipping: res.data.sameBillingShipping || res.data.data?.sameBillingShipping,
          gstChecked: res.data.gstChecked || res.data.data?.gstChecked,
          createdAt: res.data.createdAt || res.data.data?.createdAt,
          updatedAt: res.data.updatedAt || res.data.data?.updatedAt
        });
      }

      // Reset form
      setNewParty({
        name: "",
        companyName: "",
        phone: "",
        address: "",
        billingAddress: "",
        shippingAddress: "",
        sameBillingShipping: true,
        creditLimit: 0,
        GSTIN: "",
        gstChecked: true,
        currentBalance: 0,
        isActive: true  // Reset to true
      });
      setShowPartyDetails(false);
      setGstinError("");
      setPhoneError("");
      onClose();

    } catch (err) {
      console.error('AddCustomerModal: Error:', err);
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"></span>
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">Add New Customer</h3>
                {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}

                <div className="mt-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Customer Name *</label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={newParty.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Enter Customer Name"
                    required
                  />
                </div>

                <div className="mt-4">
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    id="companyName"
                    value={newParty.companyName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Enter Company Name (Optional)"
                  />
                </div>

                <div className="mt-4">
                  <label htmlFor="GSTIN" className="block text-sm font-medium text-gray-700 dark:text-gray-300">GSTIN *</label>
                  <input
                    type="text"
                    name="GSTIN"
                    id="GSTIN"
                    value={newParty.GSTIN}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Enter GSTIN (e.g., 27AAFCU5055K1ZO)"
                    required
                  />
                  {gstinError && <div className="mt-1 text-red-600 text-sm">{gstinError}</div>}
                </div>

                <div className="mt-4">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">+91</span>
                    <input
                      type="text"
                      name="phone"
                      id="phone"
                      value={newParty.phone}
                      onChange={handleInputChange}
                      className="flex-1 block w-full rounded-none rounded-r-md sm:text-sm border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder="Enter Phone Number (Optional)"
                    />
                  </div>
                  {phoneError && <div className="mt-1 text-red-600 text-sm">{phoneError}</div>}
                </div>

                <div className="mt-4">
                  <label htmlFor="currentBalance" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Opening Balance</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">₹</span>
                    </div>
                    <input
                      type="number"
                      name="currentBalance"
                      id="currentBalance"
                      value={newParty.currentBalance}
                      onChange={handleInputChange}
                      className="block w-full pl-7 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder="Enter opening balance"
                      min="0"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="creditLimit" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Credit Limit</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">₹</span>
                    </div>
                    <input
                      type="number"
                      name="creditLimit"
                      id="creditLimit"
                      value={newParty.creditLimit}
                      onChange={handleInputChange}
                      className="block w-full pl-7 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder="Enter credit limit"
                      min="0"
                    />
                  </div>
                </div>

                {/* Added back isActive checkbox */}
                <div className="mt-4">
                  <div className="flex items-center">
                    <input
                      id="isActive"
                      name="isActive"
                      type="checkbox"
                      checked={newParty.isActive}
                      onChange={handleInputChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">Active</label>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center">
                    <input
                      id="gstChecked"
                      name="gstChecked"
                      type="checkbox"
                      checked={newParty.gstChecked}
                      onChange={handleInputChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    />
                    <label htmlFor="gstChecked" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">GST Checked</label>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                    onClick={() => setShowPartyDetails(!showPartyDetails)}
                  >
                    Add Address (Optional)
                    {showPartyDetails ? (<ArrowUp className="ml-2 h-4 w-4" />) : (<ArrowDown className="ml-2 h-4 w-4" />)}
                  </button>

                  {showPartyDetails && (
                    <div className="mt-4 space-y-4">
                      <h3 className="text-md font-semibold text-gray-900 dark:text-white">Address Information</h3>

                      <div>
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                        <input
                          type="text"
                          name="address"
                          id="address"
                          value={newParty.address}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                          placeholder="Enter Address"
                        />
                      </div>

                      <div>
                        <label htmlFor="shippingAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Shipping Address</label>
                        <input
                          type="text"
                          name="shippingAddress"
                          id="shippingAddress"
                          value={newParty.shippingAddress}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                          placeholder="Enter Shipping Address"
                        />
                      </div>

                      <div>
                        <div className="flex items-center">
                          <input
                            id="sameBillingShipping"
                            name="sameBillingShipping"
                            type="checkbox"
                            checked={newParty.sameBillingShipping}
                            onChange={handleInputChange}
                            className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                          />
                          <label htmlFor="sameBillingShipping" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">Same as Address</label>
                        </div>
                      </div>

                      {!newParty.sameBillingShipping && (
                        <div>
                          <label htmlFor="billingAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Billing Address</label>
                          <input
                            type="text"
                            name="billingAddress"
                            id="billingAddress"
                            value={newParty.billingAddress}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                            placeholder="Enter Billing Address"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                    onClick={handleAddParty}
                    disabled={loading || !storeId}
                  >
                    {loading ? 'Adding...' : 'Add Customer'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}